import { Suspense, useEffect, useRef, type ReactElement, type ReactNode } from "react";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { SessionProvider, useSession } from "./lib/session";
import { ToastProvider, Loading, Skeleton, Rail } from "./components";
import { fullScreenSideways, useDesktop, usePhoneLandscape } from "./lib/useDesktop";
import { routeTable } from "./routes";
import { connect, disconnect } from "./lib/ws";
import { startTracking, stopTracking, trackView } from "./lib/track";
import { startTelemetry } from "./lib/telemetry";
import { TableInviteCard } from "./components/TableInviteCard";
import { QueueReadyCard } from "./components/QueueReadyCard";
import { TabBar } from "./components/TabBar";
import { NotificationsProvider } from "./lib/notifications";
import { startPush } from "./lib/push";
import { TabHeader } from "./screens/TabHeader";
import { UpdatePrompt } from "./components/UpdatePrompt";
import { frontDoor, markReturning } from "./lib/frontDoor";
import { NotFoundScreen } from "./screens/Welcome";
import { noteLearnRoute } from "./lib/learn";
import { UnreachableScreen } from "./screens/Unreachable";

function ScreenFallback(): ReactElement {
  return (
    <main className="screen content">
      <Loading label="Loading…"><div className="stack" style={{ paddingTop: 70 }}><Skeleton width={160} height={30} /><Skeleton height={92} /><Skeleton height={72} /></div></Loading>
    </main>
  );
}

function RequireAuth({ children }: { children: ReactNode }): ReactElement {
  const { status } = useSession();
  const location = useLocation();
  if (status === "loading") return <ScreenFallback />;
  // D-193: a first visit lands on Welcome; a phone that has been signed in before goes straight to Sign in.
  // D-199: the address keeps its query (/shop?item=…) so sign-up or sign-in can return to it.
  if (status === "signed-out") return <Navigate to={frontDoor()} replace state={{ from: location.pathname + location.search }} />;
  return <>{children}</>;
}

/** D-213: the launch screen in index.html (#boot) stays until we know who is here, then fades and goes. */
export const BOOT_MAX_MS = 4000;
function hideBoot(): void {
  const el = document.getElementById("boot");
  if (!el || el.classList.contains("boot--out")) return;
  el.classList.add("boot--out");
  setTimeout(() => el.remove(), 450);
}
export function BootDone(): null {
  const { status } = useSession();
  useEffect(() => { if (status !== "loading") hideBoot(); }, [status]);
  // A slow /session never keeps the page covered for long: the screens show their own loading state after this.
  useEffect(() => { const t = setTimeout(hideBoot, BOOT_MAX_MS); return () => clearTimeout(t); }, []);
  return null;
}

function SocketLifecycle(): null {
  const { status } = useSession();
  useEffect(() => { if (status === "signed-in") connect(); else disconnect(); }, [status]);
  // D-193: once signed in on this phone, a later signed-out visit opens Sign in rather than Welcome.
  useEffect(() => { if (status === "signed-in") markReturning(); }, [status]);
  // Turn alerts (D-084): keep this phone's push subscription with the server once notifications are allowed.
  useEffect(() => { if (status === "signed-in") void startPush(); }, [status]);
  return null;
}

/** D-215: remembers the page Learn was opened from, so closing Learn goes back there. */
function LearnReturn(): null {
  const { pathname, search } = useLocation();
  const prev = useRef<string | null>(null);
  useEffect(() => { noteLearnRoute(prev.current, pathname); prev.current = pathname + search; }, [pathname, search]);
  return null;
}

/** Usage tracking for signed-in players (D-075): screens on route change, controls on click (lib/track.ts); health reports for everyone (D-077, lib/telemetry.ts). */
function UsageTracking(): null {
  const { status } = useSession();
  const { pathname } = useLocation();
  useEffect(() => { if (status === "signed-in") startTracking(); else stopTracking(); }, [status]);
  // Health reports (D-077) come from every page, signed in or not.
  useEffect(() => { startTelemetry(); }, []);
  useEffect(() => { if (status === "signed-in") trackView(pathname); }, [status, pathname]);
  return null;
}

export function AppRoutes(): ReactElement {
  const location = useLocation();
  return (
    <ErrorBoundary resetKey={location.pathname}>
    <Suspense fallback={<ScreenFallback />}>
      <Routes>
        {routeTable.map((r) => {
          const El = r.element;
          return <Route key={r.path} path={r.path} element={r.auth ? <RequireAuth><El /></RequireAuth> : <El />} />;
        })}
        <Route path="*" element={<NotFoundScreen />} />
      </Routes>
    </Suspense>
    </ErrorBoundary>
  );
}

/** Play (table/lobby/results) and Admin draw their own rails on desktop; everything else signed-in gets the side rail. */
const OWN_RAIL = /^\/(table|lobby|results|admin)(\/|$)/;
/** Exported for tests: the rail, the header across the top and the routes, inside the app's providers. */
export function Shell(): ReactElement {
  const { status } = useSession();
  const { pathname } = useLocation();
  const desktop = useDesktop();
  const land = usePhoneLandscape();
  const rail = desktop && status === "signed-in" && !OWN_RAIL.test(pathname) && !(land && fullScreenSideways(pathname));
  return (
    <NotificationsProvider enabled={status === "signed-in"}>
      <div className={`app ${rail ? "app--rail" : ""}`} data-rail={rail ? "side" : undefined}>
        {rail && <Rail />}
        {rail && <div className="desk-top" data-testid="desk-top"><TabHeader /></div>}
        {status === "unreachable" ? <UnreachableScreen /> : <AppRoutes />}
        {status === "signed-in" && <TabBar global />}
        <TableInviteCard />
        <QueueReadyCard />
        <UpdatePrompt />
      </div>
    </NotificationsProvider>
  );
}

export function App(): ReactElement {
  return (
    <BrowserRouter>
      <SessionProvider>
        <ToastProvider>
          <BootDone />
          <SocketLifecycle />
          <UsageTracking />
          <LearnReturn />
          <Shell />
        </ToastProvider>
      </SessionProvider>
    </BrowserRouter>
  );
}
